import React, { Component, createRef } from "react"
import types from "prop-types"
import clsx from "clsx"
import { connect } from "react-redux"
import { initialize as initializeAnswerOptionsLists, PUBLIC_CONTEXT } from "reduxSlices/answerOptionsListsSlice"
import { currentUserId } from "reduxSlices/sessionSlice"
import Formsy from "formsy-react"
import { isEmpty, isEqual } from "lodash-es"
import * as API from "services/api"
import { questionTagAnswers } from "utils/formDataHelpers"
import { surveyTokenPublicDetailsShape } from "utils/propTypeShapes"
import { redirectPathAfterFormSubmission, formSubmissionVerificationPath } from "utils/routeHelpers"
import { FormIcon } from "shared/icons"
import withReduxProvider from "shared/withReduxProvider"
import withStoredQuestionTags from "shared/withStoredQuestionTags"
import { CachedQueryParamsContext, withCachedQueryParamsContext } from "contexts/CachedQueryParamsContext"
import { errorToast } from "shared/toast"
import FormTitle from "./FormElements/FormTitle"
import FormDescription from "./FormElements/FormDescription"
import FormSection from "./FormElements/FormSection"
import SubmitFormButton from "./FormElements/SubmitFormButton"
import SurveyDetails from "./FormElements/SurveyDetails"
import FilesToUploadCheckbox from "./FormElements/Attachments/FilesToUploadCheckbox"
import AuthenticatedDetails from "./FormElements/AuthenticatedDetails"

export const HAS_UPLOADS_ADDITIONAL_TEXT = "Files can be uploaded after submitting form."

class Form extends Component {
  state = {
    canSubmit: false, submitting: false, formLoadedAt: new Date(), hasUploads: false,
  }

  formRef = createRef()

  componentDidMount() {
    const { form, dispatch } = this.props

    dispatch(initializeAnswerOptionsLists({ context: PUBLIC_CONTEXT, formSlug: form.slug }))
  }

  componentDidUpdate(prevProps) {
    const { form, questionTags } = this.props
    const { questionTags: prevQuestionTags } = prevProps
    const { cachedParams } = this.context

    if (!isEqual(questionTags, prevQuestionTags)) {
      this.updateFormWithAnswers(questionTagAnswers(form, questionTags, cachedParams))
    }
  }

  enableSubmit = () => this.setState({ canSubmit: true })

  disableSubmit = () => this.setState({ canSubmit: false })

  submitForm = async (model) => {
    const { form, token } = this.props
    const { slug, requiresAuthentication } = form
    const { formLoadedAt, hasUploads } = this.state

    const submitterId = requiresAuthentication ? this.props.submitterId : null

    this.setState({ submitting: true })

    let response
    if (token) {
      response = await API.createSurveyFormSubmission({ answers: model, formLoadedAt, token })
    } else {
      response = await API.createFormSubmission({
        answers: model, formLoadedAt, slug, submitterId,
      })
    }

    const { data, ok } = response
    const { cacheKey } = data

    if (cacheKey) {
      window.location.replace(formSubmissionVerificationPath(cacheKey, hasUploads))
      return
    }

    if (!ok) {
      this.setState({ submitting: false })
      errorToast("Something went wrong. Unable to save form submission. Please correct any errors and try again")
      return
    }

    window.location.replace(redirectPathAfterFormSubmission(data, slug, hasUploads))
  }

  formAnswers = () => this.formRef.current?.getModel() || {}

  updateFormWithAnswers = (answers) => this.formRef.current?.updateInputsWithValue(answers)

  toggleHasUploads = () => {
    this.setState(({ hasUploads }) => ({ hasUploads: !hasUploads }))
  }

  render() {
    const { form, surveyDetails } = this.props
    const {
      title, description, sections, acceptsSubmittedFiles, requiresAuthentication, tracksSubmissions,
    } = form
    const { canSubmit, submitting, hasUploads } = this.state

    const renderSurveyDetails = !isEmpty(surveyDetails)

    return (
      <div>
        <div className="mx-auto mt-28 w-11/12 md:w-4/5 lg:w-3/5 mb-10 md:mb-40">
          <div className="flex flex-col items-center bg-white rounded-t-xl px-8">
            <FormIcon
              className="w-20 h-20 -mt-10 -mb-10  sm:w-24 sm:h-24 sm:-mt-12 sm:-mb-12"
              form={form}
            />
            <div
              className={clsx(
                "public-form-content flex flex-col items-center text-center",
                renderSurveyDetails && "mb-8",
              )}
            >
              <FormTitle>{title}</FormTitle>
              <FormDescription>{description}</FormDescription>
            </div>
            {
              renderSurveyDetails && (
                <SurveyDetails
                  className="form-details-box mx-16 p-4"
                  surveyDetails={surveyDetails}
                />
              )
            }
            {
              requiresAuthentication && (
                <AuthenticatedDetails
                  className="form-details-box w-full mt-2 p-4"
                  tracksSubmissions={tracksSubmissions}
                />
              )
            }
          </div>
          <Formsy
            id="form"
            ref={this.formRef}
            autoComplete="off"
            onValidSubmit={this.submitForm}
            onValid={this.enableSubmit}
            onInvalid={this.disableSubmit}
            disabled={submitting}
            preventDefaultSubmit
          >
            <div>
              <fieldset disabled={submitting}>
                {
                  sections.map((section, sectionIndex) => (
                    <FormSection
                      key={section.uuid}
                      section={section}
                      isFirstSection={sectionIndex === 0 && !acceptsSubmittedFiles}
                      isLastSection={sectionIndex === sections.length - 1}
                      formAnswers={this.formAnswers()}
                      isSubform={false}
                      canSubmit={canSubmit}
                    />
                  ))
                }
                {
                  acceptsSubmittedFiles
                  && (
                    <FilesToUploadCheckbox
                      checked={hasUploads}
                      onChange={this.toggleHasUploads}
                    />
                  )
                }

              </fieldset>
            </div>
            <div className="bg-white pb-20 rounded-b-xl">
              <SubmitFormButton canSubmit={canSubmit} submitting={submitting} />
              {
                hasUploads
                && (
                  <span className="flex justify-center mt-2 text-sm">
                    {HAS_UPLOADS_ADDITIONAL_TEXT}
                  </span>
                )
              }
            </div>
          </Formsy>
        </div>
      </div>
    )
  }
}

Form.contextType = CachedQueryParamsContext

Form.defaultProps = {
  token: "",
  surveyDetails: {},
  submitterId: null,
}

Form.propTypes = {
  dispatch: types.func.isRequired,
  form: types.shape({
    title: types.string.isRequired,
    description: types.string,
    sections: types.arrayOf(types.object),
    slug: types.string.isRequired,
    acceptsSubmittedFiles: types.bool.isRequired,
    requiresAuthentication: types.bool.isRequired,
    tracksSubmissions: types.bool.isRequired,
  }).isRequired,
  questionTags: types.arrayOf(types.shape({
    id: types.number.isRequired,
    name: types.string.isRequired,
  })).isRequired,
  submitterId: types.number,
  token: types.string,
  surveyDetails: surveyTokenPublicDetailsShape,
}

const mapStateToProps = (state) => ({
  questionTags: state.questionTags.questionTags,
  submitterId: currentUserId(state),
})

export default (
  withReduxProvider(
    withCachedQueryParamsContext(
      withStoredQuestionTags(
        connect(mapStateToProps)(Form),
      ),
    ),
  )
)
